import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

const createDefaultValue = () => ({
  name: "",
  email: "",
  phone: "",
  role: 1,
  password: "",
  createPassword: false,
  deactive: false,
});

const parseOptionalNumber = (value, fallback) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

export default function BusinessUserFormDialog({
  open,
  businessId,
  user,
  saving,
  onClose,
  onDelete,
  onResetPassword,
  onSubmit,
}) {
  const [form, setForm] = React.useState(createDefaultValue());

  React.useEffect(() => {
    if (!open) return;

    setForm({
      ...createDefaultValue(),
      ...(user || {}),
      role: user?.role ?? 1,
      password: "",
      createPassword: false,
    });
  }, [open, user]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form.name?.trim()) {
      global.showPopup("Hata", "Kullanıcı adı zorunludur.");
      return;
    }

    if (!form.email?.trim()) {
      global.showPopup("Hata", "E-posta zorunludur.");
      return;
    }

    if (!form.phone?.trim()) {
      global.showPopup("Hata", "Telefon zorunludur.");
      return;
    }

    if (!user?._id && !form.createPassword && !form.password?.trim()) {
      global.showPopup(
        "Hata",
        "Yeni kullanıcı için şifre girin veya otomatik şifre oluşturun."
      );
      return;
    }

    onSubmit({
      ...(user?._id ? { _id: user._id } : {}),
      businessId,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      role: parseOptionalNumber(form.role, 1),
      deactive: Boolean(form.deactive),
      ...(form.createPassword
        ? { createPassword: true }
        : form.password?.trim()
        ? { password: form.password.trim() }
        : {}),
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {user?._id ? "İşletme Kullanıcısını Düzenle" : "Yeni İşletme Kullanıcısı"}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: "#64748B", mb: 2 }}>
          Kullanıcının temel bilgilerini, erişim seviyesini ve giriş durumunu buradan yönetin.
        </Typography>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              label="Ad Soyad"
              fullWidth
              required
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              helperText="Panelde ve kullanıcı kayıtlarında görünecek ad."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="E-posta"
              fullWidth
              required
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              helperText="Giriş ve bildirimler için kullanılan e-posta adresi."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Telefon"
              fullWidth
              required
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              helperText="Kullanıcının kayıtlı cep telefonu numarası."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Rol Seviyesi"
              type="number"
              fullWidth
              value={form.role}
              onChange={(e) => handleChange("role", e.target.value)}
              helperText="Genelde `1` standart işletme kullanıcısını ifade eder."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ pt: { xs: 0, md: 3 } }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.deactive)}
                    onChange={(e) => handleChange("deactive", e.target.checked)}
                  />
                }
                label="Kullanıcıyı devre dışı bırak"
              />
              <Typography sx={{ fontSize: 12, color: "#64748B", ml: 4 }}>
                İşaretlenirse kullanıcı giriş yapamaz ve pasif görünür.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.createPassword)}
                    onChange={(e) => handleChange("createPassword", e.target.checked)}
                  />
                }
                label="Şifreyi sistem otomatik oluştursun"
              />
              <Typography sx={{ fontSize: 12, color: "#64748B", ml: 4 }}>
                Açık olduğunda güvenli bir şifre üretilir ve size tek seferlik gösterilir.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={user?._id ? "Yeni Şifre (isteğe bağlı)" : "Geçici Şifre"}
              type="text"
              fullWidth
              disabled={Boolean(form.createPassword)}
              value={form.password}
              onChange={(e) => handleChange("password", e.target.value)}
              helperText={
                form.createPassword
                  ? "Otomatik şifre açık olduğu için bu alan devre dışı."
                  : user?._id
                  ? "Boş bırakırsanız mevcut şifre değişmez."
                  : "Yeni kullanıcı için manuel şifre belirleyebilirsiniz."
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {user?._id && (
          <>
            <Button color="error" onClick={() => onDelete?.(user)} disabled={saving}>
              Sil
            </Button>
            <Button onClick={() => onResetPassword?.(user)} disabled={saving}>
              Şifre Sıfırla
            </Button>
          </>
        )}
        <Button onClick={onClose} disabled={saving}>
          İptal
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
